import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Flex,
  Heading,
  Text,
  Badge,
  Button,
  Spinner,
  VStack,
  HStack,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useToast,
  useColorModeValue,
  Card,
  CardBody,
  Grid,
  GridItem,
  Textarea,
  FormControl,
  FormLabel,
  Input,
  Select,
  useDisclosure,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Link as ChakraLink,
  Icon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Avatar,
  Tooltip,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from "@chakra-ui/react";
import {
  ChevronRightIcon,
  ExternalLinkIcon,
  ArrowBackIcon,
  TimeIcon,
  CheckCircleIcon,
  ViewIcon,
  DeleteIcon,
} from "@chakra-ui/icons";
import { FiPlus } from "react-icons/fi";
import api from "../services/api";
import { employeeService } from "../modules/employee/employeeService";
import { toast as toastify } from "react-toastify";
import { useTicketConstants } from "../hooks/useTicketConstants";
import {
  projectService,
  sprintService,
  milestoneService,
} from "../services/edosService";

const getDisplayName = (entity) => {
  if (!entity) return "-";
  if (entity.name) return entity.name;
  const fullName = [entity.firstName, entity.lastName]
    .filter(Boolean)
    .join(" ");
  return fullName || entity.email || entity.employeeId || "-";
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString();
};

export default function TicketDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();

  // Fetch ticket constants from server
  const {
    WORKFLOW_STATE,
    getWorkflowStateColor,
    getWorkflowStateLabel,
    getTicketWorkflowState,
    getTicketDueDate,
    getPriorityColor,
  } = useTicketConstants();

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [parentTicket, setParentTicket] = useState(null);
  const [subtasks, setSubtasks] = useState([]);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [ticketSla, setTicketSla] = useState(null);

  const [commentsLoading, setCommentsLoading] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [slaLoading, setSlaLoading] = useState(false);
  const [subtasksLoading, setSubtasksLoading] = useState(false);

  const [newComment, setNewComment] = useState("");
  const [newCommentType, setNewCommentType] = useState("public");
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentMentionQuery, setCommentMentionQuery] = useState("");
  const [commentMentionStart, setCommentMentionStart] = useState(-1);
  const [selectedCommentMentions, setSelectedCommentMentions] = useState([]);

  const [editingComment, setEditingComment] = useState(null);
  const [editCommentContent, setEditCommentContent] = useState("");

  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [fileInputRef] = useState(React.createRef());

  const {
    isOpen: isSubtaskOpen,
    onOpen: onSubtaskOpen,
    onClose: onSubtaskClose,
  } = useDisclosure();

  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    workflowState: "",
    priority: "",
    assignedTo: "",
    category: "",
    client: "",
    requestedBy: "",
    projectId: "",
    sprintId: "",
    milestoneId: "",
  });

  const [allEmployees, setAllEmployees] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [allProjects, setAllProjects] = useState([]);
  const [projectSprints, setProjectSprints] = useState([]);
  const [projectMilestones, setProjectMilestones] = useState([]);

  const [allowedTransitions, setAllowedTransitions] = useState([]);
  const [transitionsLoading, setTransitionsLoading] = useState(false);
  const [transitioningTo, setTransitioningTo] = useState(null);
  const [slaActionLoading, setSlaActionLoading] = useState("");

  // Transition modal state (for capturing required fields like assignedTo)
  const {
    isOpen: isTransitionModalOpen,
    onOpen: onTransitionModalOpen,
    onClose: onTransitionModalClose,
  } = useDisclosure();
  const [pendingTransition, setPendingTransition] = useState(null);
  const [transitionAssignee, setTransitionAssignee] = useState("");

  const cardBg = useColorModeValue("white", "gray.800");
  const cardBorder = useColorModeValue("gray.200", "gray.700");
  const tableHeaderBg = useColorModeValue("gray.100", "gray.700");
  const subText = useColorModeValue("gray.600", "gray.400");

  // Fetch ticket details
  useEffect(() => {
    loadTicketDetails();
    loadEmployeesAndClients();
  }, [id]);

  const loadTicketDetails = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/tickets/${id}`);
      if (response?.data?.data) {
        const ticketData = response.data.data;
        setTicket(ticketData);

        // Extract parent ticket if exists
        if (ticketData.parentTicket) {
          setParentTicket(ticketData.parentTicket);
        }

        // Always load subtasks
        loadSubtasks();

        // Set edit form
        setEditForm({
          title: ticketData.title || "",
          description: ticketData.description || "",
          workflowState: getTicketWorkflowState(ticketData),
          priority: ticketData.priority || "",
          assignedTo: ticketData.assignedTo?._id || "",
          category: ticketData.category || "",
          client: ticketData.client?._id || "",
          requestedBy: ticketData.requestedBy?._id || "",
          projectId: ticketData.projectId?._id || ticketData.projectId || "",
          sprintId: ticketData.sprintId?._id || ticketData.sprintId || "",
          milestoneId:
            ticketData.milestoneId?._id || ticketData.milestoneId || "",
        });

        // Load related data
        loadComments();
        loadHistory();
        loadSla();
        loadWorkflowTransitions();
      }
    } catch (error) {
      console.error("Error loading ticket:", error);
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to load ticket",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeesAndClients = async () => {
    try {
      const [employeesResult, clientsResult, projectsResult] =
        await Promise.allSettled([
          employeeService.getAllEmployees(),
          api.get("/clients/getAllClients"),
          projectService.list(),
        ]);

      if (employeesResult.status === "fulfilled") {
        const rows =
          employeesResult.value?.data?.data ||
          employeesResult.value?.data ||
          employeesResult.value ||
          [];
        setAllEmployees(Array.isArray(rows) ? rows : []);
      }
      if (clientsResult.status === "fulfilled") {
        const rows =
          clientsResult.value?.data?.data ||
          clientsResult.value?.data?.clients ||
          clientsResult.value?.data ||
          [];
        setAllClients(Array.isArray(rows) ? rows : []);
      }
      if (projectsResult.status === "fulfilled") {
        const rows =
          projectsResult.value?.data?.data ||
          projectsResult.value?.data?.projects ||
          [];
        setAllProjects(Array.isArray(rows) ? rows : []);
      }
    } catch (error) {
      console.error("Error loading employees/clients:", error);
    }
  };

  const loadComments = async () => {
    try {
      setCommentsLoading(true);
      const response = await api.get(`/api/tickets/${id}/comments`);
      if (response?.data?.data) {
        setComments(response.data.data);
      }
    } catch (error) {
      console.error("Error loading comments:", error);
    } finally {
      setCommentsLoading(false);
    }
  };

  const loadHistory = async () => {
    try {
      setHistoryLoading(true);
      const response = await api.get(`/api/tickets/${id}/history`);
      if (response?.data?.data) {
        setHistory(response.data.data);
      }
    } catch (error) {
      console.error("Error loading history:", error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadSla = async () => {
    try {
      setSlaLoading(true);
      const response = await api.get(`/api/tickets/${id}/sla`);
      if (response?.data?.data) {
        setTicketSla(response.data.data);
      }
    } catch (error) {
      console.error("Error loading SLA:", error);
    } finally {
      setSlaLoading(false);
    }
  };

  const loadSubtasks = async () => {
    try {
      setSubtasksLoading(true);
      const response = await api.get(`/api/tickets/${id}/subtasks`);
      if (response?.data?.data) {
        setSubtasks(response.data.data);
      }
    } catch (error) {
      console.error("Error loading subtasks:", error);
    } finally {
      setSubtasksLoading(false);
    }
  };

  const loadWorkflowTransitions = async () => {
    try {
      setTransitionsLoading(true);
      const response = await api.get(`/api/tickets/${id}/workflow/transitions`);
      if (response?.data?.data?.allowedTransitions) {
        setAllowedTransitions(response.data.data.allowedTransitions);
      }
    } catch (error) {
      console.error("Error loading workflow transitions:", error);
    } finally {
      setTransitionsLoading(false);
    }
  };

  useEffect(() => {
    const loadProjectScopedItems = async () => {
      if (!editForm.projectId) {
        setProjectSprints([]);
        setProjectMilestones([]);
        return;
      }

      try {
        const [sprintsRes, milestonesRes] = await Promise.all([
          sprintService.listByProject(editForm.projectId),
          milestoneService.listByProject(editForm.projectId),
        ]);

        setProjectSprints(
          sprintsRes?.data?.data || sprintsRes?.data?.sprints || [],
        );
        setProjectMilestones(
          milestonesRes?.data?.data || milestonesRes?.data?.milestones || [],
        );
      } catch (error) {
        console.error("Error loading project scoped items:", error);
        setProjectSprints([]);
        setProjectMilestones([]);
      }
    };

    loadProjectScopedItems();
  }, [editForm.projectId]);

  const handleWorkflowTransition = async (toState, requiredFields = []) => {
    // Debug logging
    console.log("handleWorkflowTransition called:", {
      toState,
      requiredFields,
      ticketAssignedTo: ticket?.assignedTo,
      needsAssignee:
        requiredFields.includes("assignedTo") && !ticket?.assignedTo?._id,
    });

    // Check if this transition requires assignedTo and ticket doesn't have one
    const needsAssignee =
      requiredFields.includes("assignedTo") && !ticket?.assignedTo?._id;

    if (needsAssignee) {
      // Open modal to collect required fields
      setPendingTransition({ toState, requiredFields });
      setTransitionAssignee("");
      onTransitionModalOpen();
      return;
    }

    // Proceed with transition
    await executeTransition(toState);
  };

  const executeTransition = async (toState, additionalData = {}) => {
    try {
      setTransitioningTo(toState);

      // Include any additional data (like assignedTo) directly in the transition request
      // The backend will apply these fields BEFORE validating the transition
      const transitionPayload = { toState };
      if (additionalData.assignedTo) {
        transitionPayload.assignedTo = additionalData.assignedTo;
      }

      // Debug logging
      console.log("executeTransition payload:", transitionPayload);

      await api.post(
        `/api/tickets/${id}/workflow/transition`,
        transitionPayload,
      );

      // Reload all ticket data after successful transition
      await loadTicketDetails();
      toast({
        title: `Status changed to "${getWorkflowStateLabel(toState)}"`,
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      // Reload to show current state
      await loadTicketDetails();
      toast({
        title: "Transition failed",
        description: error.response?.data?.message || "Could not change status",
        status: "error",
        duration: 5000,
      });
    } finally {
      setTransitioningTo(null);
    }
  };

  const handleTransitionModalSubmit = async () => {
    if (!pendingTransition) return;

    // Validate required fields
    if (
      pendingTransition.requiredFields.includes("assignedTo") &&
      !transitionAssignee
    ) {
      toast({
        title: "Assignee required",
        description:
          "Please select an assignee to proceed with this transition",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    onTransitionModalClose();
    await executeTransition(pendingTransition.toState, {
      assignedTo: transitionAssignee,
    });
    setPendingTransition(null);
    setTransitionAssignee("");
  };

  const handleSlaAction = async (action) => {
    try {
      setSlaActionLoading(action);
      const url = `/api/tickets/${id}/sla/${action}`;
      const body = action === "pause" ? { reason: "Paused by user" } : {};
      const response = await api.post(url, body);
      if (response?.data?.data) {
        setTicketSla(response.data.data);
      }
      toast({
        title: `SLA ${action}d successfully`,
        status: "success",
        duration: 3000,
      });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || `Failed to ${action} SLA`,
        status: "error",
        duration: 5000,
      });
    } finally {
      setSlaActionLoading("");
    }
  };

  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast({
        title: "Comment cannot be empty",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    try {
      setSubmittingComment(true);

      // Keep only mentions that still exist in the current comment content.
      const mentionIds = selectedCommentMentions
        .filter((mention) =>
          newComment.toLowerCase().includes(`@${mention.label.toLowerCase()}`),
        )
        .map((mention) => mention.id);

      await api.post(`/api/tickets/${id}/comments`, {
        content: newComment,
        commentType: newCommentType,
        mentions: mentionIds,
      });
      setNewComment("");
      setNewCommentType("public");
      setCommentMentionQuery("");
      setCommentMentionStart(-1);
      setSelectedCommentMentions([]);
      await loadComments();
      toast({ title: "Comment added", status: "success", duration: 3000 });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to add comment",
        status: "error",
        duration: 5000,
      });
    } finally {
      setSubmittingComment(false);
    }
  };

  const getEmployeeMentionLabel = (employee) => {
    if (!employee) return "";
    const fullName = [employee.firstName, employee.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    return fullName || employee.name || employee.email || employee.employeeId;
  };

  const detectMentionQuery = (value, caretPosition) => {
    const uptoCaret = value.slice(0, caretPosition);
    const atIndex = uptoCaret.lastIndexOf("@");

    if (atIndex < 0) {
      setCommentMentionQuery("");
      setCommentMentionStart(-1);
      return;
    }

    // Mention is valid only at text start or after whitespace.
    if (atIndex > 0 && !/\s/.test(uptoCaret[atIndex - 1])) {
      setCommentMentionQuery("");
      setCommentMentionStart(-1);
      return;
    }

    const query = uptoCaret.slice(atIndex + 1);
    if (/\s/.test(query)) {
      setCommentMentionQuery("");
      setCommentMentionStart(-1);
      return;
    }

    setCommentMentionQuery(query);
    setCommentMentionStart(atIndex);
  };

  const handleCommentInputChange = (event) => {
    const value = event.target.value;
    const caretPosition = event.target.selectionStart || value.length;
    setNewComment(value);
    detectMentionQuery(value, caretPosition);
  };

  const handleSelectMention = (employee) => {
    const label = getEmployeeMentionLabel(employee);
    if (!label || commentMentionStart < 0) return;

    const nextComment =
      newComment.slice(0, commentMentionStart) +
      `@${label} ` +
      newComment.slice(commentMentionStart + commentMentionQuery.length + 1);

    setNewComment(nextComment);
    setCommentMentionQuery("");
    setCommentMentionStart(-1);

    setSelectedCommentMentions((prev) => {
      if (prev.some((mention) => mention.id === employee._id)) {
        return prev;
      }

      return [
        ...prev,
        {
          id: employee._id,
          label,
        },
      ];
    });
  };

  const handleRemoveMention = (mentionId) => {
    setSelectedCommentMentions((prev) =>
      prev.filter((mention) => mention.id !== mentionId),
    );
  };

  const filteredMentionEmployees =
    commentMentionStart >= 0
      ? allEmployees
          .filter((employee) => {
            const label = getEmployeeMentionLabel(employee).toLowerCase();
            return label.includes(commentMentionQuery.toLowerCase());
          })
          .slice(0, 6)
      : [];

  const handleDeleteComment = async (commentId) => {
    const doDelete = async () => {
      try {
        await api.delete(`/api/tickets/${id}/comments/${commentId}`);
        await loadComments();
        toastify.success("Comment deleted", { autoClose: 3000 });
      } catch (error) {
        toastify.error(
          error.response?.data?.message || "Failed to delete comment",
          { autoClose: 5000 },
        );
      }
    };

    const ConfirmToast = ({ closeToast }) => (
      <div style={{ fontSize: 14 }}>
        <p style={{ margin: "0 0 10px" }}>Delete this comment?</p>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            onClick={() => {
              closeToast();
              doDelete();
            }}
            style={{
              background: "#e53e3e",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              padding: "5px 14px",
              cursor: "pointer",
              fontWeight: 600,
            }}
          >
            Delete
          </button>
          <button
            onClick={closeToast}
            style={{
              background: "#e2e8f0",
              color: "#2d3748",
              border: "none",
              borderRadius: 4,
              padding: "5px 14px",
              cursor: "pointer",
            }}
          >
            Cancel
          </button>
        </div>
      </div>
    );

    toastify(<ConfirmToast />, {
      autoClose: false,
      closeOnClick: false,
      draggable: false,
      position: "top-center",
      closeButton: false,
    });
  };

  const handleSaveEdit = async () => {
    try {
      setIsSaving(true);
      const payload = {
        title: editForm.title,
        description: editForm.description,
        priority: editForm.priority,
        assignedTo: editForm.assignedTo || null,
        category: editForm.category,
        projectId: editForm.projectId || null,
        sprintId: editForm.sprintId || null,
        milestoneId: editForm.milestoneId || null,
      };
      await api.patch(`/api/tickets/${id}`, payload);
      await loadTicketDetails();
      setIsEditing(false);
      toast({ title: "Ticket updated", status: "success", duration: 3000 });
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to update ticket",
        status: "error",
        duration: 5000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    // Reset editForm to ticket data
    if (ticket) {
      setEditForm({
        title: ticket.title || "",
        description: ticket.description || "",
        workflowState: getTicketWorkflowState(ticket),
        priority: ticket.priority || "",
        assignedTo: ticket.assignedTo?._id || "",
        category: ticket.category || "",
        client: ticket.client?._id || "",
        requestedBy: ticket.requestedBy?._id || "",
        projectId: ticket.projectId?._id || ticket.projectId || "",
        sprintId: ticket.sprintId?._id || ticket.sprintId || "",
        milestoneId: ticket.milestoneId?._id || ticket.milestoneId || "",
      });
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file || !ticket) {
      return;
    }

    try {
      setUploadingAttachment(true);
      const formData = new FormData();
      formData.append("file", file);

      const response = await api.post(
        `/api/tickets/${ticket._id}/attachments`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      if (response.data?.success) {
        toast({
          title: "Attachment uploaded successfully",
          status: "success",
          duration: 2000,
        });

        // Refresh ticket data to show new attachment
        await loadTicketDetails();

        // Reset file input
        if (fileInputRef.current) {
          fileInputRef.current.value = "";
        }
      }
    } catch (error) {
      console.error("File upload failed:", error);
      toast({
        title: "Failed to upload attachment",
        description: error.response?.data?.message || "Please try again later.",
        status: "error",
        duration: 3000,
      });
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleDeleteAttachment = async (attachmentUrl) => {
    if (!ticket) {
      return;
    }

    const shouldDelete = window.confirm(
      "Delete this attachment? This action cannot be undone.",
    );

    if (!shouldDelete) {
      return;
    }

    try {
      const response = await api.delete(
        `/api/tickets/${ticket._id}/attachments`,
        {
          data: { attachmentUrl },
        },
      );

      if (response.data?.success) {
        toast({
          title: "Attachment deleted successfully",
          status: "success",
          duration: 2000,
        });

        // Refresh ticket data
        await loadTicketDetails();
      }
    } catch (error) {
      console.error("Delete attachment failed:", error);
      toast({
        title: "Failed to delete attachment",
        description: error.response?.data?.message || "Please try again later.",
        status: "error",
        duration: 3000,
      });
    }
  };

  const handleDeleteTicket = async () => {
    if (!window.confirm("Delete this ticket? This action cannot be undone."))
      return;
    try {
      await api.delete(`/api/tickets/${id}`);
      toast({ title: "Ticket deleted", status: "success", duration: 3000 });
      setTimeout(() => navigate("/Ticketing"), 500);
    } catch (error) {
      toast({
        title: "Error",
        description: error.response?.data?.message || "Failed to delete ticket",
        status: "error",
        duration: 5000,
      });
    }
  };

  const handleCreateSubtask = async (subtaskData) => {
    try {
      await api.post(`/api/tickets/${id}/subtasks`, subtaskData);
      await loadSubtasks();
      onSubtaskClose();
      toast({ title: "Subtask created", status: "success", duration: 3000 });
    } catch (error) {
      toast({
        title: "Error",
        description:
          error.response?.data?.message || "Failed to create subtask",
        status: "error",
        duration: 5000,
      });
    }
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" minHeight="100vh">
        <Spinner size="xl" color="teal.500" />
      </Flex>
    );
  }

  if (!ticket) {
    return (
      <Box p={8}>
        <Button
          leftIcon={<ArrowBackIcon />}
          onClick={() => navigate("/Ticketing")}
        >
          Back to Tickets
        </Button>
        <Text mt={4} color="red.500">
          Ticket not found
        </Text>
      </Box>
    );
  }

  return (
    <Box p={8} bg={useColorModeValue("gray.50", "gray.900")} minHeight="100vh">
      {/* Breadcrumb */}
      <Breadcrumb mb={6} separator={<ChevronRightIcon color="gray.500" />}>
        <BreadcrumbItem>
          <BreadcrumbLink onClick={() => navigate("/Ticketing")}>
            Tickets
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem isCurrentPage>
          <Text>{ticket.ticketCode}</Text>
        </BreadcrumbItem>
      </Breadcrumb>

      {/* Back button and header */}
      <Flex justify="space-between" align="start" mb={6}>
        <VStack align="start" spacing={2} flex="1">
          <Button
            variant="outline"
            leftIcon={<ArrowBackIcon />}
            onClick={() => navigate("/Ticketing")}
          >
            Back
          </Button>
          <HStack>
            <Heading size="lg">{ticket.ticketCode}</Heading>
            {!isEditing && (
              <>
                <Badge
                  colorScheme={getWorkflowStateColor(
                    getTicketWorkflowState(ticket),
                  )}
                >
                  {getWorkflowStateLabel(getTicketWorkflowState(ticket))}
                </Badge>
                <Badge colorScheme={getPriorityColor(ticket.priority)}>
                  {ticket.priority}
                </Badge>
              </>
            )}
          </HStack>
          {!isEditing && (
            <Text fontSize="md" fontWeight="500">
              {ticket.title}
            </Text>
          )}
          {isEditing && (
            <Input
              size="md"
              placeholder="Ticket title"
              value={editForm.title}
              onChange={(e) =>
                setEditForm({ ...editForm, title: e.target.value })
              }
            />
          )}
        </VStack>
        <HStack>
          {!isEditing ? (
            <>
              <Button colorScheme="blue" onClick={() => setIsEditing(true)}>
                Edit
              </Button>
              <Button
                colorScheme="red"
                variant="outline"
                onClick={handleDeleteTicket}
              >
                Delete
              </Button>
            </>
          ) : (
            <>
              <Button
                colorScheme="green"
                onClick={handleSaveEdit}
                isLoading={isSaving}
              >
                Save
              </Button>
              <Button
                variant="outline"
                onClick={handleCancelEdit}
                isDisabled={isSaving}
              >
                Cancel
              </Button>
            </>
          )}
        </HStack>
      </Flex>

      {/* Parent ticket banner (if exists) */}
      {parentTicket && (
        <Card bg="blue.50" border="1px" borderColor="blue.200" mb={6}>
          <CardBody>
            <Flex align="center" justify="space-between">
              <VStack align="start" spacing={1}>
                <Flex align="center" gap={2}>
                  <Icon as={ChevronRightIcon} color="blue.700" boxSize={4} />
                  <Text fontWeight="600" color="blue.700">
                    Subtask of parent ticket
                  </Text>
                </Flex>
                <ChakraLink
                  color="blue.600"
                  fontWeight="500"
                  onClick={() => navigate(`/Ticketing/${parentTicket._id}`)}
                  cursor="pointer"
                  display="flex"
                  alignItems="center"
                  gap={2}
                >
                  {parentTicket.ticketCode}: {parentTicket.title}
                  <ExternalLinkIcon boxSize={4} />
                </ChakraLink>
              </VStack>
            </Flex>
          </CardBody>
        </Card>
      )}

      {/* Subtasks section */}
      <Card
        bg={useColorModeValue("teal.50", "gray.700")}
        border="1px"
        borderColor="teal.200"
        mb={6}
      >
        <CardBody>
          <VStack align="start" spacing={3} width="100%">
            <Flex justify="space-between" width="100%" align="center">
              <Flex align="center" gap={2}>
                <ViewIcon
                  color={useColorModeValue("teal.700", "teal.200")}
                  boxSize={5}
                />
                <Text
                  fontWeight="600"
                  color={useColorModeValue("teal.700", "teal.200")}
                >
                  Subtasks ({subtasks.length})
                </Text>
              </Flex>
              <Button size="sm" colorScheme="teal" onClick={onSubtaskOpen}>
                + New Subtask
              </Button>
            </Flex>
            {subtasksLoading ? (
              <Spinner size="sm" />
            ) : subtasks.length > 0 ? (
              <VStack spacing={2} width="100%">
                {subtasks.map((subtask) => (
                  <Box
                    key={subtask._id}
                    p={3}
                    bg={useColorModeValue("white", "gray.800")}
                    borderRadius="md"
                    width="100%"
                    cursor="pointer"
                    onClick={() => navigate(`/Ticketing/${subtask._id}`)}
                    _hover={{ shadow: "md" }}
                    transition="shadow 0.2s"
                  >
                    <Flex justify="space-between" align="center">
                      <VStack align="start" spacing={1} flex="1">
                        <Flex align="center" gap={2}>
                          <Text fontWeight="600">{subtask.ticketCode}</Text>
                          <Badge
                            colorScheme={getWorkflowStateColor(
                              getTicketWorkflowState(subtask),
                            )}
                          >
                            {getWorkflowStateLabel(
                              getTicketWorkflowState(subtask),
                            )}
                          </Badge>
                          <Badge
                            colorScheme={getPriorityColor(subtask.priority)}
                            variant="outline"
                            fontSize="0.65em"
                          >
                            {subtask.priority}
                          </Badge>
                        </Flex>
                        <Text fontSize="sm">{subtask.title}</Text>
                      </VStack>
                      <ExternalLinkIcon ml={2} />
                    </Flex>
                  </Box>
                ))}
              </VStack>
            ) : (
              <Text fontSize="sm" color="gray.500">
                No subtasks yet. Click "+ New Subtask" to add one.
              </Text>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Main content grid */}
      <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={6} mb={6}>
        {/* Left column - Details and tabs */}
        <VStack spacing={6} align="stretch">
          {/* Quick info card */}
          <Card bg={cardBg} borderColor={cardBorder} borderWidth="1px">
            <CardBody>
              <Grid templateColumns="1fr 1fr" gap={4}>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">
                    Status
                  </FormLabel>
                  {!isEditing ? (
                    <Badge
                      colorScheme={getWorkflowStateColor(
                        getTicketWorkflowState(ticket),
                      )}
                    >
                      {getWorkflowStateLabel(getTicketWorkflowState(ticket))}
                    </Badge>
                  ) : (
                    <Menu>
                      <MenuButton
                        as={Button}
                        size="sm"
                        variant="outline"
                        width="100%"
                        justifyContent="start"
                        bg={useColorModeValue("white", "gray.700")}
                        color={useColorModeValue("black", "white")}
                      >
                        {getWorkflowStateLabel(editForm.workflowState) ||
                          "Select status"}
                      </MenuButton>
                      <MenuList
                        bg={useColorModeValue("white", "gray.700")}
                        borderColor={useColorModeValue("gray.300", "gray.600")}
                        color={useColorModeValue("black", "white")}
                      >
                        <MenuItem
                          bg={useColorModeValue("white", "gray.700")}
                          color={useColorModeValue("black", "white")}
                          _hover={{
                            bg: useColorModeValue("gray.100", "gray.600"),
                          }}
                          onClick={() =>
                            setEditForm({
                              ...editForm,
                              workflowState: WORKFLOW_STATE.OPEN,
                            })
                          }
                        >
                          <Text color={useColorModeValue("black", "white")}>
                            Open
                          </Text>
                        </MenuItem>
                        <MenuItem
                          bg={useColorModeValue("white", "gray.700")}
                          color={useColorModeValue("black", "white")}
                          _hover={{
                            bg: useColorModeValue("gray.100", "gray.600"),
                          }}
                          onClick={() =>
                            setEditForm({
                              ...editForm,
                              workflowState: WORKFLOW_STATE.IN_PROGRESS,
                            })
                          }
                        >
                          <Text color={useColorModeValue("black", "white")}>
                            In Progress
                          </Text>
                        </MenuItem>
                        <MenuItem
                          bg={useColorModeValue("white", "gray.700")}
                          color={useColorModeValue("black", "white")}
                          _hover={{
                            bg: useColorModeValue("gray.100", "gray.600"),
                          }}
                          onClick={() =>
                            setEditForm({
                              ...editForm,
                              workflowState: WORKFLOW_STATE.ON_HOLD,
                            })
                          }
                        >
                          <Text color={useColorModeValue("black", "white")}>
                            On Hold
                          </Text>
                        </MenuItem>
                        <MenuItem
                          bg={useColorModeValue("white", "gray.700")}
                          color={useColorModeValue("black", "white")}
                          _hover={{
                            bg: useColorModeValue("gray.100", "gray.600"),
                          }}
                          onClick={() =>
                            setEditForm({
                              ...editForm,
                              workflowState: WORKFLOW_STATE.RESOLVED,
                            })
                          }
                        >
                          <Text color={useColorModeValue("black", "white")}>
                            Resolved
                          </Text>
                        </MenuItem>
                        <MenuItem
                          bg={useColorModeValue("white", "gray.700")}
                          color={useColorModeValue("black", "white")}
                          _hover={{
                            bg: useColorModeValue("gray.100", "gray.600"),
                          }}
                          onClick={() =>
                            setEditForm({
                              ...editForm,
                              workflowState: WORKFLOW_STATE.CLOSED,
                            })
                          }
                        >
                          <Text color={useColorModeValue("black", "white")}>
                            Closed
                          </Text>
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">
                    Priority
                  </FormLabel>
                  {!isEditing ? (
                    <Badge colorScheme={getPriorityColor(ticket.priority)}>
                      {ticket.priority}
                    </Badge>
                  ) : (
                    <Menu>
                      <MenuButton
                        as={Button}
                        size="sm"
                        variant="outline"
                        width="100%"
                        justifyContent="start"
                        bg={useColorModeValue("white", "gray.700")}
                        color={useColorModeValue("black", "white")}
                      >
                        {editForm.priority || "Select priority"}
                      </MenuButton>
                      <MenuList
                        bg={useColorModeValue("white", "gray.700")}
                        borderColor={useColorModeValue("gray.300", "gray.600")}
                        color={useColorModeValue("black", "white")}
                      >
                        <MenuItem
                          bg={useColorModeValue("white", "gray.700")}
                          color={useColorModeValue("black", "white")}
                          _hover={{
                            bg: useColorModeValue("gray.100", "gray.600"),
                          }}
                          onClick={() =>
                            setEditForm({ ...editForm, priority: "Low" })
                          }
                        >
                          <Text color={useColorModeValue("black", "white")}>
                            Low
                          </Text>
                        </MenuItem>
                        <MenuItem
                          bg={useColorModeValue("white", "gray.700")}
                          color={useColorModeValue("black", "white")}
                          _hover={{
                            bg: useColorModeValue("gray.100", "gray.600"),
                          }}
                          onClick={() =>
                            setEditForm({ ...editForm, priority: "Medium" })
                          }
                        >
                          <Text color={useColorModeValue("black", "white")}>
                            Medium
                          </Text>
                        </MenuItem>
                        <MenuItem
                          bg={useColorModeValue("white", "gray.700")}
                          color={useColorModeValue("black", "white")}
                          _hover={{
                            bg: useColorModeValue("gray.100", "gray.600"),
                          }}
                          onClick={() =>
                            setEditForm({ ...editForm, priority: "High" })
                          }
                        >
                          <Text color={useColorModeValue("black", "white")}>
                            High
                          </Text>
                        </MenuItem>
                        <MenuItem
                          bg={useColorModeValue("white", "gray.700")}
                          color={useColorModeValue("black", "white")}
                          _hover={{
                            bg: useColorModeValue("gray.100", "gray.600"),
                          }}
                          onClick={() =>
                            setEditForm({ ...editForm, priority: "Critical" })
                          }
                        >
                          <Text color={useColorModeValue("black", "white")}>
                            Critical
                          </Text>
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">
                    Requested By
                  </FormLabel>
                  {!isEditing ? (
                    <Text>{getDisplayName(ticket.requestedBy)}</Text>
                  ) : (
                    <Menu>
                      <MenuButton
                        as={Button}
                        size="sm"
                        variant="outline"
                        width="100%"
                        justifyContent="start"
                        bg={useColorModeValue("white", "gray.700")}
                        color={useColorModeValue("black", "white")}
                      >
                        {editForm.requestedBy
                          ? getDisplayName(
                              allEmployees.find(
                                (e) => e._id === editForm.requestedBy,
                              ),
                            )
                          : "Select an employee"}
                      </MenuButton>
                      <MenuList
                        bg={useColorModeValue("white", "gray.700")}
                        borderColor={useColorModeValue("gray.300", "gray.600")}
                        color={useColorModeValue("black", "white")}
                        maxH="300px"
                        overflowY="auto"
                      >
                        {allEmployees.map((emp) => (
                          <MenuItem
                            key={emp._id}
                            bg={useColorModeValue("white", "gray.700")}
                            color={useColorModeValue("black", "white")}
                            _hover={{
                              bg: useColorModeValue("gray.100", "gray.600"),
                            }}
                            onClick={() =>
                              setEditForm({
                                ...editForm,
                                requestedBy: emp._id,
                              })
                            }
                          >
                            <Text color={useColorModeValue("black", "white")}>
                              {emp.firstName} {emp.lastName}
                            </Text>
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">
                    Assigned To
                  </FormLabel>
                  {!isEditing ? (
                    <Text>{getDisplayName(ticket.assignedTo)}</Text>
                  ) : (
                    <Menu>
                      <MenuButton
                        as={Button}
                        size="sm"
                        variant="outline"
                        width="100%"
                        justifyContent="start"
                        bg={useColorModeValue("white", "gray.700")}
                      >
                        {editForm.assignedTo
                          ? getDisplayName(
                              allEmployees.find(
                                (e) => e._id === editForm.assignedTo,
                              ),
                            )
                          : "Select an employee"}
                      </MenuButton>
                      <MenuList
                        bg={useColorModeValue("white", "gray.700")}
                        borderColor={useColorModeValue("gray.300", "gray.600")}
                        color={useColorModeValue("black", "white")}
                        maxH="300px"
                        overflowY="auto"
                      >
                        {allEmployees.map((emp) => (
                          <MenuItem
                            key={emp._id}
                            bg={useColorModeValue("white", "gray.700")}
                            color={useColorModeValue("black", "white")}
                            _hover={{
                              bg: useColorModeValue("gray.100", "gray.600"),
                            }}
                            onClick={() =>
                              setEditForm({
                                ...editForm,
                                assignedTo: emp._id,
                              })
                            }
                          >
                            <Text color={useColorModeValue("black", "white")}>
                              {emp.firstName} {emp.lastName}
                            </Text>
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">
                    Client
                  </FormLabel>
                  {!isEditing ? (
                    <Text>{getDisplayName(ticket.client)}</Text>
                  ) : (
                    <Menu>
                      <MenuButton
                        as={Button}
                        size="sm"
                        variant="outline"
                        width="100%"
                        justifyContent="start"
                        bg={useColorModeValue("white", "gray.700")}
                        color={useColorModeValue("black", "white")}
                      >
                        {editForm.client
                          ? getDisplayName(
                              allClients.find((c) => c._id === editForm.client),
                            )
                          : "Select a client"}
                      </MenuButton>
                      <MenuList
                        bg={useColorModeValue("white", "gray.700")}
                        borderColor={useColorModeValue("gray.300", "gray.600")}
                        color={useColorModeValue("black", "white")}
                        maxH="300px"
                        overflowY="auto"
                      >
                        {allClients.map((client) => (
                          <MenuItem
                            key={client._id}
                            bg={useColorModeValue("white", "gray.700")}
                            color={useColorModeValue("black", "white")}
                            _hover={{
                              bg: useColorModeValue("gray.100", "gray.600"),
                            }}
                            onClick={() =>
                              setEditForm({
                                ...editForm,
                                client: client._id,
                              })
                            }
                          >
                            <Text color={useColorModeValue("black", "white")}>
                              {client.name}
                            </Text>
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">
                    Category
                  </FormLabel>
                  {!isEditing ? (
                    <Text>{ticket.category || "-"}</Text>
                  ) : (
                    <Input
                      value={editForm.category}
                      onChange={(e) =>
                        setEditForm({ ...editForm, category: e.target.value })
                      }
                      placeholder="Category"
                      size="sm"
                    />
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">
                    Project
                  </FormLabel>
                  {!isEditing ? (
                    <Text>{ticket.projectId?.projectName || "-"}</Text>
                  ) : (
                    <Select
                      value={editForm.projectId}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          projectId: e.target.value,
                          sprintId: "",
                          milestoneId: "",
                        })
                      }
                      placeholder="Select project"
                      size="sm"
                    >
                      {allProjects.map((project) => (
                        <option key={project._id} value={project._id}>
                          {project.projectName || project.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">
                    Sprint
                  </FormLabel>
                  {!isEditing ? (
                    <Text>{ticket.sprintId?.sprintName || "-"}</Text>
                  ) : (
                    <Select
                      value={editForm.sprintId}
                      onChange={(e) =>
                        setEditForm({ ...editForm, sprintId: e.target.value })
                      }
                      placeholder="Select sprint"
                      size="sm"
                      isDisabled={!editForm.projectId}
                    >
                      {projectSprints.map((sprint) => (
                        <option key={sprint._id} value={sprint._id}>
                          {sprint.sprintName || sprint.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">
                    Milestone
                  </FormLabel>
                  {!isEditing ? (
                    <Text>{ticket.milestoneId?.title || "-"}</Text>
                  ) : (
                    <Select
                      value={editForm.milestoneId}
                      onChange={(e) =>
                        setEditForm({
                          ...editForm,
                          milestoneId: e.target.value,
                        })
                      }
                      placeholder="Select milestone"
                      size="sm"
                      isDisabled={!editForm.projectId}
                    >
                      {projectMilestones.map((milestone) => (
                        <option key={milestone._id} value={milestone._id}>
                          {milestone.title || milestone.name}
                        </option>
                      ))}
                    </Select>
                  )}
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">
                    Created
                  </FormLabel>
                  <Text>{formatDate(ticket.createdAt)}</Text>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm" fontWeight="600">
                    Updated
                  </FormLabel>
                  <Text>{formatDate(ticket.updatedAt)}</Text>
                </FormControl>
              </Grid>
            </CardBody>
          </Card>

          {/* Description */}
          <Card bg={cardBg} borderColor={cardBorder} borderWidth="1px">
            <CardBody>
              <FormControl>
                <FormLabel fontWeight="600">Description</FormLabel>
                {!isEditing ? (
                  <Box
                    p={3}
                    bg={useColorModeValue("gray.50", "gray.900")}
                    borderRadius="md"
                    fontSize="sm"
                    color={subText}
                    whiteSpace="pre-wrap"
                    wordBreak="break-word"
                  >
                    {ticket.description || "No description provided"}
                  </Box>
                ) : (
                  <Textarea
                    value={editForm.description}
                    onChange={(e) =>
                      setEditForm({
                        ...editForm,
                        description: e.target.value,
                      })
                    }
                    placeholder="Enter ticket description"
                    minH="150px"
                  />
                )}
              </FormControl>
            </CardBody>
          </Card>

          {/* Attachments */}
          <Card bg={cardBg} borderColor={cardBorder} borderWidth="1px">
            <CardBody>
              <FormControl>
                <FormLabel fontWeight="600">Attachments</FormLabel>
                {ticket.attachments && ticket.attachments.length > 0 ? (
                  <VStack align="stretch" spacing={2} mb={3}>
                    {ticket.attachments.map((attachment, index) => (
                      <Flex
                        key={`${attachment}-${index}`}
                        align="center"
                        justify="space-between"
                        bg={useColorModeValue("gray.50", "gray.900")}
                        p={3}
                        borderRadius="md"
                      >
                        <HStack spacing={3} flex={1}>
                          <Badge colorScheme="blue" fontSize="xs">
                            {attachment.split(".").pop().toUpperCase()}
                          </Badge>
                          <ChakraLink
                            href={attachment}
                            isExternal
                            color="blue.500"
                            fontSize="sm"
                            isTruncated
                            maxW="400px"
                            _hover={{ textDecoration: "underline" }}
                          >
                            {attachment.split("/").pop()}
                          </ChakraLink>
                          <Icon
                            as={ExternalLinkIcon}
                            boxSize={3}
                            color="gray.400"
                          />
                        </HStack>
                        {isEditing && (
                          <Tooltip label="Delete attachment">
                            <Button
                              size="xs"
                              colorScheme="red"
                              variant="ghost"
                              onClick={() => handleDeleteAttachment(attachment)}
                            >
                              <DeleteIcon />
                            </Button>
                          </Tooltip>
                        )}
                      </Flex>
                    ))}
                  </VStack>
                ) : (
                  <Text fontSize="sm" color={subText} mb={3}>
                    No attachments yet
                  </Text>
                )}
                {isEditing && (
                  <Box>
                    <Input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleFileUpload}
                      accept=".jpeg,.jpg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
                      size="sm"
                      display="none"
                    />
                    <HStack spacing={2}>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        onClick={() => fileInputRef.current?.click()}
                        isLoading={uploadingAttachment}
                        leftIcon={<FiPlus />}
                      >
                        Upload File
                      </Button>
                      <Text fontSize="xs" color={subText}>
                        Max 10MB • PDF, Images, Documents, Archives
                      </Text>
                    </HStack>
                  </Box>
                )}
              </FormControl>
            </CardBody>
          </Card>

          {/* Tabs */}
          <Tabs colorScheme="teal">
            <TabList>
              <Tab>Comments</Tab>
              <Tab>History</Tab>
              <Tab>SLA</Tab>
            </TabList>

            <TabPanels>
              {/* Comments Tab */}
              <TabPanel px={0}>
                <VStack spacing={0} align="stretch">
                  {/* Comments list */}
                  {commentsLoading ? (
                    <Flex justify="center" py={6}>
                      <Spinner size="sm" />
                    </Flex>
                  ) : comments.length === 0 ? (
                    <Flex
                      direction="column"
                      align="center"
                      py={10}
                      color={subText}
                    >
                      <Text fontSize="sm">
                        No comments yet. Be the first to comment.
                      </Text>
                    </Flex>
                  ) : (
                    <VStack spacing={0} align="stretch" mb={6}>
                      {comments.map((comment, idx) => {
                        const name = getDisplayName(comment.createdBy);
                        const initials = name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")
                          .toUpperCase()
                          .slice(0, 2);
                        const isInternal = comment.commentType === "internal";
                        const ts = comment.createdAt
                          ? new Date(comment.createdAt).toLocaleString(
                              undefined,
                              {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              },
                            )
                          : "";
                        return (
                          <Flex
                            key={comment._id}
                            gap={3}
                            px={4}
                            py={4}
                            borderBottom="1px solid"
                            borderColor={cardBorder}
                            bg={
                              isInternal
                                ? useColorModeValue("yellow.50", "yellow.900")
                                : "transparent"
                            }
                            _hover={{
                              bg: isInternal
                                ? useColorModeValue("yellow.100", "yellow.800")
                                : useColorModeValue("gray.50", "gray.750"),
                            }}
                            role="group"
                          >
                            {/* Avatar */}
                            <Avatar
                              size="sm"
                              name={name}
                              bg={isInternal ? "yellow.400" : "teal.500"}
                              color="white"
                              flexShrink={0}
                            />

                            {/* Body */}
                            <Box flex="1" minW={0}>
                              <Flex
                                align="center"
                                gap={2}
                                mb={1}
                                flexWrap="wrap"
                              >
                                <Text fontWeight="700" fontSize="sm">
                                  {name}
                                </Text>
                                {isInternal && (
                                  <Badge colorScheme="yellow" fontSize="0.65em">
                                    Internal
                                  </Badge>
                                )}
                                {comment.isEdited && (
                                  <Text fontSize="xs" color={subText}>
                                    (edited)
                                  </Text>
                                )}
                                <Tooltip
                                  label={new Date(
                                    comment.createdAt,
                                  ).toLocaleString()}
                                  placement="top"
                                >
                                  <Text
                                    fontSize="xs"
                                    color={subText}
                                    cursor="default"
                                  >
                                    {ts}
                                  </Text>
                                </Tooltip>
                              </Flex>
                              <Text
                                fontSize="sm"
                                whiteSpace="pre-wrap"
                                wordBreak="break-word"
                              >
                                {comment.content}
                              </Text>
                            </Box>

                            {/* Actions — visible on row hover */}
                            <HStack
                              spacing={1}
                              opacity={0}
                              _groupHover={{ opacity: 1 }}
                              transition="opacity 0.15s"
                              alignSelf="flex-start"
                              mt={0.5}
                            >
                              <Tooltip label="Delete comment">
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="red"
                                  onClick={() =>
                                    handleDeleteComment(comment._id)
                                  }
                                >
                                  Delete
                                </Button>
                              </Tooltip>
                            </HStack>
                          </Flex>
                        );
                      })}
                    </VStack>
                  )}

                  {/* Add comment form */}
                  <Box
                    px={4}
                    pt={4}
                    borderTop="1px solid"
                    borderColor={cardBorder}
                  >
                    <Flex gap={3} align="flex-start">
                      <Avatar
                        size="sm"
                        bg="teal.500"
                        color="white"
                        flexShrink={0}
                      />
                      <Box flex="1">
                        <Textarea
                          placeholder="Add a comment... Type @ to mention users"
                          value={newComment}
                          onChange={handleCommentInputChange}
                          size="sm"
                          borderRadius="md"
                          minH="80px"
                          mb={2}
                          focusBorderColor="teal.400"
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                              handleAddComment();
                            }
                          }}
                        />
                        {commentMentionStart >= 0 && (
                          <Box
                            mb={2}
                            border="1px solid"
                            borderColor={cardBorder}
                            borderRadius="md"
                            bg={useColorModeValue("white", "gray.700")}
                            maxH="180px"
                            overflowY="auto"
                          >
                            {filteredMentionEmployees.length === 0 ? (
                              <Text px={3} py={2} fontSize="xs" color={subText}>
                                No users found for "@{commentMentionQuery}"
                              </Text>
                            ) : (
                              filteredMentionEmployees.map((employee) => {
                                const label = getEmployeeMentionLabel(employee);
                                return (
                                  <Button
                                    key={employee._id}
                                    variant="ghost"
                                    justifyContent="flex-start"
                                    w="100%"
                                    borderRadius={0}
                                    size="sm"
                                    onClick={() =>
                                      handleSelectMention(employee)
                                    }
                                  >
                                    @{label}
                                  </Button>
                                );
                              })
                            )}
                          </Box>
                        )}
                        {selectedCommentMentions.length > 0 && (
                          <HStack spacing={2} mb={2} flexWrap="wrap">
                            {selectedCommentMentions.map((mention) => (
                              <Badge
                                key={mention.id}
                                colorScheme="teal"
                                borderRadius="full"
                                px={2}
                                py={1}
                              >
                                @{mention.label}
                                <Button
                                  ml={1}
                                  size="xs"
                                  variant="ghost"
                                  minW="auto"
                                  h="16px"
                                  p={0}
                                  onClick={() =>
                                    handleRemoveMention(mention.id)
                                  }
                                  aria-label={`Remove mention ${mention.label}`}
                                >
                                  x
                                </Button>
                              </Badge>
                            ))}
                          </HStack>
                        )}
                        <Flex justify="space-between" align="center">
                          <Select
                            size="xs"
                            width="130px"
                            value={newCommentType}
                            onChange={(e) => setNewCommentType(e.target.value)}
                            borderRadius="md"
                          >
                            <option value="public">Public</option>
                            <option value="internal">Internal</option>
                          </Select>
                          <HStack spacing={2}>
                            <Text fontSize="xs" color={subText}>
                              ⌘↵ to submit
                            </Text>
                            <Button
                              colorScheme="teal"
                              size="sm"
                              onClick={handleAddComment}
                              isLoading={submittingComment}
                              isDisabled={!newComment.trim()}
                            >
                              Save
                            </Button>
                          </HStack>
                        </Flex>
                      </Box>
                    </Flex>
                  </Box>
                </VStack>
              </TabPanel>

              {/* History Tab */}
              <TabPanel px={0} pt={2}>
                {historyLoading ? (
                  <Flex justify="center" py={6}>
                    <Spinner size="sm" />
                  </Flex>
                ) : history.length === 0 ? (
                  <Flex justify="center" py={10}>
                    <Text fontSize="sm" color={subText}>
                      No activity recorded yet.
                    </Text>
                  </Flex>
                ) : (
                  <VStack spacing={0} align="stretch">
                    {history.map((item, idx) => {
                      const actor = getDisplayName(item.changedBy);

                      // Resolve a raw field value to a human-readable string
                      const resolveFieldValue = (field, value) => {
                        if (value == null || value === "") return "—";

                        // If already a populated object, use getDisplayName directly
                        if (
                          typeof value === "object" &&
                          !Array.isArray(value)
                        ) {
                          return getDisplayName(value);
                        }

                        const strVal = String(value);

                        // Date fields
                        if (field === "slaDueDate" || field === "dueDate") {
                          const d = new Date(value);
                          return isNaN(d.getTime())
                            ? strVal
                            : d.toLocaleString(undefined, {
                                month: "short",
                                day: "numeric",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              });
                        }
                        // ID fields — look up in employees then clients
                        if (
                          field === "assignedTo" ||
                          field === "requestedBy" ||
                          field === "changedBy"
                        ) {
                          const emp = allEmployees.find(
                            (e) => String(e._id) === strVal,
                          );
                          if (emp) return getDisplayName(emp);
                        }
                        if (field === "client") {
                          const cl = allClients.find(
                            (c) => String(c._id) === strVal,
                          );
                          if (cl) return getDisplayName(cl);
                          const emp = allEmployees.find(
                            (e) => String(e._id) === strVal,
                          );
                          if (emp) return getDisplayName(emp);
                        }
                        return strVal;
                      };
                      const ts = item.createdAt
                        ? new Date(item.createdAt).toLocaleString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : item.formattedTimestamp || "";

                      // Human-readable change type label + color
                      const changeConfig = {
                        created: {
                          label: "created this ticket",
                          color: "green.500",
                          dot: "green.400",
                        },
                        updated: {
                          label: "updated this ticket",
                          color: "blue.500",
                          dot: "blue.400",
                        },
                        status_changed: {
                          label: "changed the status",
                          color: "purple.500",
                          dot: "purple.400",
                        },
                        priority_changed: {
                          label: "changed the priority",
                          color: "orange.500",
                          dot: "orange.400",
                        },
                        assignee_changed: {
                          label: "changed the assignee",
                          color: "teal.500",
                          dot: "teal.400",
                        },
                        assigned: {
                          label: "assigned this ticket",
                          color: "teal.500",
                          dot: "teal.400",
                        },
                        reassigned: {
                          label: "reassigned this ticket",
                          color: "teal.500",
                          dot: "teal.400",
                        },
                        comment_added: {
                          label: "added a comment",
                          color: "gray.500",
                          dot: "gray.400",
                        },
                        reopened: {
                          label: "reopened this ticket",
                          color: "yellow.600",
                          dot: "yellow.400",
                        },
                        closed: {
                          label: "closed this ticket",
                          color: "red.500",
                          dot: "red.400",
                        },
                        deleted: {
                          label: "deleted this ticket",
                          color: "red.600",
                          dot: "red.500",
                        },
                      };
                      const cfg = changeConfig[item.changeType] || {
                        label:
                          item.changeType?.replace(/_/g, " ") ||
                          "made a change",
                        color: "gray.500",
                        dot: "gray.400",
                      };

                      const isLast = idx === history.length - 1;

                      return (
                        <Flex key={item._id || idx} gap={0} px={4}>
                          {/* Timeline spine */}
                          <Flex
                            direction="column"
                            align="center"
                            mr={3}
                            flexShrink={0}
                          >
                            <Box
                              w="10px"
                              h="10px"
                              borderRadius="full"
                              bg={cfg.dot}
                              mt="14px"
                              flexShrink={0}
                              border="2px solid"
                              borderColor={useColorModeValue(
                                "white",
                                "gray.800",
                              )}
                              boxShadow={`0 0 0 2px var(--chakra-colors-${cfg.dot.replace(".", "-")})`}
                            />
                            {!isLast && (
                              <Box
                                w="2px"
                                flex="1"
                                minH="24px"
                                bg={useColorModeValue("gray.200", "gray.600")}
                              />
                            )}
                          </Flex>

                          {/* Content */}
                          <Box py={3} flex="1" minW={0}>
                            <Flex align="baseline" gap={1} flexWrap="wrap">
                              <Text fontWeight="700" fontSize="sm">
                                {actor}
                              </Text>
                              <Text fontSize="sm" color={cfg.color}>
                                {cfg.label}
                              </Text>
                              <Tooltip
                                label={new Date(
                                  item.createdAt,
                                ).toLocaleString()}
                                placement="top"
                              >
                                <Text
                                  fontSize="xs"
                                  color={subText}
                                  cursor="default"
                                  ml={1}
                                >
                                  · {ts}
                                </Text>
                              </Tooltip>
                            </Flex>

                            {/* Field-level changes */}
                            {item.changes?.length > 0 && (
                              <VStack mt={2} spacing={1} align="stretch">
                                {item.changes.map((ch, ci) => (
                                  <Flex
                                    key={ci}
                                    align="center"
                                    gap={2}
                                    flexWrap="wrap"
                                    bg={useColorModeValue(
                                      "gray.50",
                                      "gray.750",
                                    )}
                                    px={3}
                                    py={1.5}
                                    borderRadius="md"
                                    fontSize="xs"
                                  >
                                    <Badge
                                      colorScheme="gray"
                                      fontWeight="600"
                                      textTransform="capitalize"
                                    >
                                      {ch.field
                                        ?.replace(/([A-Z])/g, " $1")
                                        .trim()}
                                    </Badge>
                                    {ch.oldValue && (
                                      <>
                                        <Text
                                          color="red.500"
                                          textDecoration="line-through"
                                          maxW="160px"
                                          overflow="hidden"
                                          textOverflow="ellipsis"
                                          whiteSpace="nowrap"
                                        >
                                          {resolveFieldValue(
                                            ch.field,
                                            ch.oldValue,
                                          )}
                                        </Text>
                                        <Text color={subText}>→</Text>
                                      </>
                                    )}
                                    <Text
                                      color="green.500"
                                      fontWeight="600"
                                      maxW="160px"
                                      overflow="hidden"
                                      textOverflow="ellipsis"
                                      whiteSpace="nowrap"
                                    >
                                      {resolveFieldValue(ch.field, ch.newValue)}
                                    </Text>
                                  </Flex>
                                ))}
                              </VStack>
                            )}

                            {/* Metadata pill for created */}
                            {item.changeType === "created" &&
                              item.metadata?.title && (
                                <Flex mt={2} gap={2} flexWrap="wrap">
                                  <Badge colorScheme="green">
                                    {item.metadata.status}
                                  </Badge>
                                  <Badge colorScheme="orange">
                                    {item.metadata.priority}
                                  </Badge>
                                </Flex>
                              )}
                          </Box>
                        </Flex>
                      );
                    })}
                  </VStack>
                )}
              </TabPanel>

              {/* SLA Tab */}
              <TabPanel px={0} pt={2}>
                {slaLoading ? (
                  <Flex justify="center" py={6}>
                    <Spinner size="sm" />
                  </Flex>
                ) : !ticketSla ? (
                  <Flex justify="center" py={10}>
                    <Text fontSize="sm" color={subText}>
                      No SLA information available.
                    </Text>
                  </Flex>
                ) : (
                  (() => {
                    const overallStatus = ticketSla.isBreached
                      ? "breached"
                      : ticketSla.isPaused
                        ? "paused"
                        : ticketSla.response?.status === "breached" ||
                            ticketSla.resolution?.status === "breached"
                          ? "breached"
                          : ticketSla.response?.percentageUsed > 80 ||
                              ticketSla.resolution?.percentageUsed > 80
                            ? "at_risk"
                            : "active";

                    const statusColors = {
                      breached: { scheme: "red", label: "Breached" },
                      at_risk: { scheme: "orange", label: "At Risk" },
                      paused: { scheme: "gray", label: "Paused" },
                      active: { scheme: "green", label: "On Track" },
                      achieved: { scheme: "teal", label: "Achieved" },
                      met: { scheme: "teal", label: "Met" },
                    };

                    const fmt = (iso) => {
                      if (!iso) return "—";
                      const d = new Date(iso);
                      return isNaN(d.getTime())
                        ? iso
                        : d.toLocaleString(undefined, {
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                          });
                    };

                    const fmtMins = (mins) => {
                      if (mins == null) return "—";
                      if (mins <= 0) return "Overdue";
                      const h = Math.floor(mins / 60);
                      const m = mins % 60;
                      return h > 0 ? `${h}h ${m}m` : `${m}m`;
                    };

                    const SLABar = ({ pct, status }) => {
                      const color =
                        status === "breached"
                          ? "red.500"
                          : pct > 80
                            ? "orange.400"
                            : "teal.400";
                      return (
                        <Box
                          w="full"
                          h="6px"
                          bg={useColorModeValue("gray.200", "gray.600")}
                          borderRadius="full"
                          overflow="hidden"
                        >
                          <Box
                            h="full"
                            w={`${Math.min(pct || 0, 100)}%`}
                            bg={color}
                            borderRadius="full"
                            transition="width 0.4s"
                          />
                        </Box>
                      );
                    };

                    const SLARow = ({ label, data }) => {
                      if (!data) return null;
                      const cfg =
                        statusColors[data.status] || statusColors.active;
                      return (
                        <Box
                          p={4}
                          borderRadius="lg"
                          border="1px solid"
                          borderColor={cardBorder}
                          bg={cardBg}
                        >
                          <Flex justify="space-between" align="center" mb={3}>
                            <Flex align="center" gap={2}>
                              {label.includes("Response") && (
                                <TimeIcon boxSize={4} />
                              )}
                              {label.includes("Resolution") && (
                                <CheckCircleIcon boxSize={4} />
                              )}
                              <Text fontWeight="700" fontSize="sm">
                                {label.replace(/⚡|✅/g, "").trim()}
                              </Text>
                            </Flex>
                            <Badge
                              colorScheme={cfg.scheme}
                              borderRadius="full"
                              px={2}
                            >
                              {cfg.label}
                            </Badge>
                          </Flex>

                          <SLABar
                            pct={data.percentageUsed}
                            status={data.status}
                          />

                          <Grid templateColumns="repeat(3, 1fr)" gap={3} mt={3}>
                            <Box>
                              <Text fontSize="xs" color={subText} mb={0.5}>
                                Target
                              </Text>
                              <Text fontSize="sm" fontWeight="600">
                                {data.target != null
                                  ? fmtMins(data.target)
                                  : "—"}
                              </Text>
                            </Box>
                            <Box>
                              <Text fontSize="xs" color={subText} mb={0.5}>
                                Deadline
                              </Text>
                              <Text fontSize="sm" fontWeight="600">
                                {fmt(data.deadline)}
                              </Text>
                            </Box>
                            <Box>
                              <Text fontSize="xs" color={subText} mb={0.5}>
                                {data.status === "breached"
                                  ? "Overdue by"
                                  : "Remaining"}
                              </Text>
                              <Text
                                fontSize="sm"
                                fontWeight="700"
                                color={
                                  data.status === "breached"
                                    ? "red.500"
                                    : data.remaining < 60
                                      ? "orange.500"
                                      : "green.500"
                                }
                              >
                                {fmtMins(data.remaining)}
                              </Text>
                            </Box>
                          </Grid>

                          {data.percentageUsed != null && (
                            <Text fontSize="xs" color={subText} mt={2}>
                              {Math.round(data.percentageUsed)}% of SLA time
                              used
                            </Text>
                          )}
                        </Box>
                      );
                    };

                    return (
                      <VStack spacing={4} align="stretch" px={4} pt={2}>
                        {/* Overall status banner */}
                        <Flex
                          align="center"
                          gap={3}
                          p={4}
                          borderRadius="lg"
                          bg={
                            overallStatus === "breached"
                              ? useColorModeValue("red.50", "red.900")
                              : overallStatus === "at_risk"
                                ? useColorModeValue("orange.50", "orange.900")
                                : overallStatus === "paused"
                                  ? useColorModeValue("gray.100", "gray.700")
                                  : useColorModeValue("green.50", "green.900")
                          }
                          border="1px solid"
                          borderColor={
                            overallStatus === "breached"
                              ? "red.300"
                              : overallStatus === "at_risk"
                                ? "orange.300"
                                : overallStatus === "paused"
                                  ? "gray.300"
                                  : "green.300"
                          }
                        >
                          <Box
                            w="10px"
                            h="10px"
                            borderRadius="full"
                            flexShrink={0}
                            bg={
                              overallStatus === "breached"
                                ? "red.500"
                                : overallStatus === "at_risk"
                                  ? "orange.400"
                                  : overallStatus === "paused"
                                    ? "gray.400"
                                    : "green.400"
                            }
                          />
                          <Box>
                            <Text fontWeight="700" fontSize="sm">
                              {overallStatus === "breached"
                                ? "SLA Breached"
                                : overallStatus === "at_risk"
                                  ? "SLA At Risk — over 80% time used"
                                  : overallStatus === "paused"
                                    ? "SLA Paused"
                                    : "SLA On Track"}
                            </Text>
                            <Text fontSize="xs" color={subText}>
                              Priority: {ticketSla.priority}
                              {ticketSla.isPaused && "  ·  Paused"}
                            </Text>
                          </Box>
                        </Flex>

                        {/* SLA Actions */}
                        <HStack spacing={2} mt={3} flexWrap="wrap">
                          {ticketSla.isPaused ? (
                            <Button
                              size="xs"
                              colorScheme="green"
                              isLoading={slaActionLoading === "resume"}
                              isDisabled={!!slaActionLoading}
                              onClick={() => handleSlaAction("resume")}
                            >
                              Resume SLA
                            </Button>
                          ) : (
                            <Button
                              size="xs"
                              colorScheme="yellow"
                              isLoading={slaActionLoading === "pause"}
                              isDisabled={!!slaActionLoading}
                              onClick={() => handleSlaAction("pause")}
                            >
                              Pause SLA
                            </Button>
                          )}
                          <Button
                            size="xs"
                            variant="outline"
                            colorScheme="gray"
                            isLoading={slaActionLoading === "calculate"}
                            isDisabled={!!slaActionLoading}
                            onClick={() => handleSlaAction("calculate")}
                          >
                            Recalculate
                          </Button>
                        </HStack>

                        {/* Response & Resolution rows */}
                        <SLARow
                          label="Response SLA"
                          data={ticketSla.response}
                        />
                        <SLARow
                          label="Resolution SLA"
                          data={ticketSla.resolution}
                        />

                        {/* Breach detail */}
                        {ticketSla.isBreached &&
                          ticketSla.breachDetails?.breachedAt && (
                            <Box
                              p={3}
                              borderRadius="md"
                              bg={useColorModeValue("red.50", "red.900")}
                              border="1px solid"
                              borderColor="red.300"
                              fontSize="sm"
                            >
                              <Text fontWeight="600" color="red.600" mb={1}>
                                Breach Details
                              </Text>
                              <Text>
                                Breached at:{" "}
                                {fmt(ticketSla.breachDetails.breachedAt)}
                              </Text>
                              {ticketSla.breachDetails.breachDuration > 0 && (
                                <Text>
                                  Duration over deadline:{" "}
                                  {fmtMins(
                                    ticketSla.breachDetails.breachDuration,
                                  )}
                                </Text>
                              )}
                            </Box>
                          )}
                      </VStack>
                    );
                  })()
                )}
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>

        {/* Right column - Activity sidebar */}
        <VStack spacing={4} align="stretch">
          {/* Workflow Transitions */}
          <Card bg={cardBg} borderColor={cardBorder} borderWidth="1px">
            <CardBody>
              <Heading size="sm" mb={3}>
                Workflow
              </Heading>
              <Box mb={3}>
                <Text fontSize="xs" color={subText} mb={1}>
                  Current Status
                </Text>
                <Badge
                  colorScheme={getWorkflowStateColor(
                    getTicketWorkflowState(ticket),
                  )}
                  fontSize="sm"
                  px={2}
                  py={0.5}
                >
                  {getWorkflowStateLabel(getTicketWorkflowState(ticket))}
                </Badge>
              </Box>
              <Divider mb={3} />
              {transitionsLoading ? (
                <Flex justify="center" py={3}>
                  <Spinner size="sm" />
                </Flex>
              ) : allowedTransitions.length === 0 ? (
                <Text fontSize="xs" color={subText}>
                  No transitions available
                </Text>
              ) : (
                <VStack spacing={2} align="stretch">
                  <Text
                    fontSize="xs"
                    color={subText}
                    fontWeight="600"
                    textTransform="uppercase"
                    letterSpacing="wide"
                  >
                    Move to
                  </Text>
                  {allowedTransitions.map(
                    ({ toState, action, requiredFields }) => {
                      const colorMap = {
                        in_progress: "orange",
                        on_hold: "yellow",
                        resolved: "green",
                        closed: "gray",
                        open: "blue",
                      };
                      const scheme = colorMap[toState] || "gray";
                      const needsAssignee =
                        requiredFields?.includes("assignedTo") &&
                        !ticket?.assignedTo?._id;
                      return (
                        <Button
                          key={toState}
                          size="sm"
                          colorScheme={scheme}
                          variant="outline"
                          justifyContent="flex-start"
                          isLoading={transitioningTo === toState}
                          isDisabled={transitioningTo !== null}
                          onClick={() =>
                            handleWorkflowTransition(toState, requiredFields)
                          }
                          rightIcon={
                            needsAssignee ? (
                              <Badge
                                colorScheme="orange"
                                fontSize="0.6em"
                                ml={1}
                              >
                                Needs Assignee
                              </Badge>
                            ) : null
                          }
                        >
                          {getWorkflowStateLabel(toState)}
                        </Button>
                      );
                    },
                  )}
                </VStack>
              )}
            </CardBody>
          </Card>

          {/* Quick Stats */}
          <Card bg={cardBg} borderColor={cardBorder} borderWidth="1px">
            <CardBody>
              <Heading size="sm" mb={4}>
                Quick Stats
              </Heading>
              <VStack spacing={3} align="stretch">
                <Box>
                  <Text fontSize="xs" color={subText} mb={1}>
                    Priority
                  </Text>
                  <Badge colorScheme={getPriorityColor(ticket.priority)}>
                    {ticket.priority}
                  </Badge>
                </Box>
                <Divider />
                <Box>
                  <Text fontSize="xs" color={subText} mb={1}>
                    Total Comments
                  </Text>
                  <Text fontWeight="600">{comments.length}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color={subText} mb={1}>
                    Subtasks
                  </Text>
                  <Text fontWeight="600">{subtasks.length}</Text>
                </Box>
                {ticket.parentTicket && (
                  <Box>
                    <Text fontSize="xs" color={subText} mb={1}>
                      Parent Ticket
                    </Text>
                    <ChakraLink
                      fontSize="sm"
                      color="blue.500"
                      fontWeight="600"
                      onClick={() =>
                        navigate(`/Ticketing/${parentTicket?._id}`)
                      }
                      cursor="pointer"
                    >
                      {parentTicket?.ticketCode || "View parent"}
                    </ChakraLink>
                  </Box>
                )}
                <Divider />
                <Box>
                  <Text fontSize="xs" color={subText} mb={1}>
                    Created
                  </Text>
                  <Text fontSize="sm">{formatDate(ticket.createdAt)}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color={subText} mb={1}>
                    Updated
                  </Text>
                  <Text fontSize="sm">{formatDate(ticket.updatedAt)}</Text>
                </Box>
              </VStack>
            </CardBody>
          </Card>
        </VStack>
      </Grid>

      {/* Transition Assignee Modal */}
      <Modal isOpen={isTransitionModalOpen} onClose={onTransitionModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Assign Ticket Before Transition</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text mb={4} color="gray.600">
              This transition requires an assignee. Please select someone to
              assign this ticket to before moving it to{" "}
              <Badge colorScheme="blue">
                {getWorkflowStateLabel(pendingTransition?.toState)}
              </Badge>
            </Text>
            <FormControl isRequired>
              <FormLabel>Assign To</FormLabel>
              <Select
                placeholder="Select an employee"
                value={transitionAssignee}
                onChange={(e) => setTransitionAssignee(e.target.value)}
              >
                {allEmployees.map((emp) => (
                  <option key={emp._id} value={emp._id}>
                    {emp.firstName} {emp.lastName}
                    {emp.email ? ` (${emp.email})` : ""}
                  </option>
                ))}
              </Select>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onTransitionModalClose}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleTransitionModalSubmit}
              isLoading={transitioningTo !== null}
              isDisabled={!transitionAssignee}
            >
              Assign & Transition
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create Subtask Modal */}
      <SubtaskModal
        isOpen={isSubtaskOpen}
        onClose={onSubtaskClose}
        parentTicket={ticket}
        onCreate={handleCreateSubtask}
      />
    </Box>
  );
}

// Subtask creation modal component
function SubtaskModal({ isOpen, onClose, parentTicket, onCreate }) {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "Medium",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      alert("Title is required");
      return;
    }
    try {
      setSubmitting(true);
      await onCreate(formData);
      setFormData({ title: "", description: "", priority: "Medium" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Create Subtask</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={4}>
            <FormControl>
              <FormLabel>Title</FormLabel>
              <Input
                placeholder="Subtask title..."
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel>Description</FormLabel>
              <Textarea
                placeholder="Optional description..."
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
              />
            </FormControl>
            <FormControl>
              <FormLabel>Priority</FormLabel>
              <Select
                value={formData.priority}
                onChange={(e) =>
                  setFormData({ ...formData, priority: e.target.value })
                }
              >
                <option value="Low">Low</option>
                <option value="Medium">Medium</option>
                <option value="High">High</option>
                <option value="Critical">Critical</option>
              </Select>
            </FormControl>
          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="teal"
            onClick={handleSubmit}
            isLoading={submitting}
          >
            Create Subtask
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
